import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMG } from "@/data/site";
import QuoteCTA from "@/components/QuoteCTA";
import FlyerCard from "@/components/FlyerCard";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

const CAPABILITIES = [
  "Sawmilling", "Kiln drying", "Timber processing", "Joinery",
  "Furniture manufacturing", "Shopfitting", "Export logistics", "Brisbane warehousing & distribution",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="px-5 pb-20 pt-32 md:px-10 md:pt-44">
        <div className="mx-auto max-w-[1500px]">
          <Kicker>About Mr Kwila</Kicker>
          <h1 className="mt-4 max-w-5xl font-display text-5xl font-light leading-[0.95] tracking-tight md:text-7xl">
            <MaskedLine delay={0.1}>From PNG forests to</MaskedLine>
            <MaskedLine delay={0.22}>Australian backyards.</MaskedLine>
          </h1>
          <Reveal delay={0.3} className="mt-8 max-w-md">
            <FlyerCard />
          </Reveal>
        </div>
      </section>

      <section className="px-5 pb-24 md:px-10">
        <div className="mx-auto grid max-w-[1500px] gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="overflow-hidden rounded-sm">
              <img src="/about-hero.png" alt="Designer hardwood timber panelling in a bedroom interior" className="aspect-[16/10] w-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <h2 className="font-display text-3xl font-light tracking-tight md:text-4xl">
                Mr Kwila Outdoor Furniture Pty Ltd
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-ink/70">
                <p>
                  The furniture is manufactured in Papua New Guinea by one of the country's largest and most
                  respected timber groups — a company founded over 40 years ago by Sir Robert Sinclair Kt., who
                  remains the resident Managing Director of Mr Kwila to this day.
                </p>
                <p>
                  The group brings together sawmilling, timber processing, kiln drying, joinery, furniture
                  manufacturing and shopfitting under one roof, with the head office — Furniture Exports (PNG)
                  Ltd — based in Lae, Morobe Province. The Kwila used in the outdoor furniture range is sourced
                  from the managed forest areas of Papua New Guinea. [Sourcing documentation to be supplied by
                  client.]
                </p>
                <p>
                  The PNG export division supplies our Australian division, based at Archerfield, Brisbane, for
                  warehousing and distribution — bringing kiln-dried sawn timber, processed structural timber,
                  outdoor furniture, solid timber doors, decking and posts directly to Australian retail and
                  trade customers.
                </p>
                <p>
                  One group controls the journey from sawmilling to delivery, which means consistent stock,
                  honest lead times and a single point of accountability.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-stone/50 px-5 py-20 md:px-10 md:py-24" data-testid="factory-gallery">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-10">
            <Kicker>Inside the PNG Operation</Kicker>
            <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">The Lae factory</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { src: "/about/factory-interior-1.jpg", alt: "Mr Kwila factory production floor, Lae PNG", cap: "Production floor" },
              { src: "/about/factory-exterior.jpg", alt: "Mr Kwila Outdoor Furniture factory building, Lae PNG", cap: "Head office & factory, Lae" },
              { src: "/about/factory-interior-2.jpg", alt: "Mr Kwila factory timber stock, Lae PNG", cap: "Timber stock & dispatch" },
            ].map((f, i) => (
              <Reveal key={f.src} delay={i * 0.08}>
                <figure>
                  <div className="overflow-hidden rounded-sm bg-white">
                    <img
                      src={f.src}
                      alt={f.alt}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-sand">
                    {f.cap}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      <section className="bg-ink px-5 py-24 text-cream md:px-10 md:py-32" data-testid="capabilities-section">
        <div className="mx-auto max-w-[1500px]">
          <Reveal className="mb-12">
            <Kicker dark>Group Capability</Kicker>
            <h2 className="mt-3 max-w-3xl font-display text-4xl font-light leading-tight tracking-tight md:text-6xl">
              Everything under one roof — from log to lounge.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-cream/15 bg-cream/15 md:grid-cols-4">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.05} className="bg-ink p-6 md:p-8">
                <span className="text-xs font-bold text-kwila">0{i + 1}</span>
                <p className="mt-3 font-display text-xl leading-tight md:text-2xl">{c}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <Link
              to="/trade"
              data-testid="about-trade-link"
              className="mt-12 inline-flex items-center gap-2 rounded-full bg-cream px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-kwila hover:text-cream"
            >
              Become a Trade Partner <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="px-5 py-24 md:px-10">
        <div className="mx-auto grid max-w-[1500px] items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>Australian Base</Kicker>
              <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">
                Archerfield, Brisbane
              </h2>
              <p className="mt-5 text-base leading-relaxed text-ink/70">
                Our Queensland warehouse holds stock for retail orders, trade supply and project freight.
                Pickup is available by arrangement, and delivery is quoted per order for bulky items.
              </p>
              <Link
                to="/contact"
                data-testid="about-contact-link"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila"
              >
                Contact the Team <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="overflow-hidden rounded-t-[160px]">
              <img src="/about-australia.png" alt="Australian home with hardwood cladding, decking and pool at dusk" loading="lazy" className="aspect-[16/10] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
